import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, Instagram, Youtube, MapPin } from "lucide-react";
import { SITE, COURSES, BRAND_IMAGE } from "@/site";

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="relative bg-black border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
                    {/* Brand */}
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="relative flex items-center justify-center h-12 w-14 shrink-0">
                                <img src={BRAND_IMAGE} alt="IHAO MUSIC HOUSE DELHI" className="w-full h-full object-contain" />
                            </span>
                            <div>
                                <div className="font-display font-semibold text-white text-sm whitespace-nowrap tracking-tight">Ihao Music House Delhi</div>
                            </div>
                        </div>
                        <p className="text-sm text-white/60 leading-relaxed max-w-xs">
                            A premium performance-first music academy in Malviya Nagar, New Delhi — training the next generation of musicians.
                        </p>
                        <div className="flex items-center gap-3 mt-6">
                            <a href={SITE.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:border-gold hover:text-gold transition-colors">
                                <Instagram className="w-4 h-4" />
                            </a>
                            <a href={SITE.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="footer-youtube" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:border-gold hover:text-gold transition-colors">
                                <Youtube className="w-4 h-4" />
                            </a>
                            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="footer-whatsapp" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:border-gold hover:text-gold transition-colors">
                                <MessageCircle className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Explore */}
                    <div>
                        <h4 className="text-white font-display font-semibold text-base mb-5">Explore</h4>
                        <ul className="space-y-3 text-sm">
                            {[
                                ["/", "Home"],
                                ["/about", "About"],
                                ["/courses", "Courses"],
                                ["/blog", "Blog"],
                                ["/gallery", "Gallery"],
                                ["/shop", "Store"],
                                ["/contact", "Contact"],
                            ].map(([href, label]) => (
                                <li key={href}>
                                    <Link to={href} className="text-white/60 hover:text-gold transition-colors">{label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Courses */}
                    <div>
                        <h4 className="text-white font-display font-semibold text-base mb-5">Popular Courses</h4>
                        <ul className="space-y-3 text-sm">
                            {COURSES.slice(0, 6).map((c) => (
                                <li key={c.slug}>
                                    <Link to={`/courses/${c.slug}`} className="text-white/60 hover:text-gold transition-colors">{c.name}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Locations */}
                    <div>
                        <h4 className="text-white font-display font-semibold text-base mb-5">Service Areas</h4>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/locations/saket" className="text-white/60 hover:text-gold transition-colors">Music Classes in Saket</Link></li>
                            <li><Link to="/locations/hauz-khas" className="text-white/60 hover:text-gold transition-colors">Music Classes in Hauz Khas</Link></li>
                            <li><Link to="/locations/greater-kailash" className="text-white/60 hover:text-gold transition-colors">Music Classes in GK</Link></li>
                            <li><Link to="/blog" className="text-white/60 hover:text-gold transition-colors">Music Blog</Link></li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-white font-display font-semibold text-base mb-5">Get in Touch</h4>
                        <ul className="space-y-4 text-sm">
                            <li className="flex items-start gap-3 text-white/70">
                                <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                                <a href={SITE.mapLink} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">{SITE.address}</a>
                            </li>
                            <li className="flex items-center gap-3 text-white/70">
                                <Phone className="w-4 h-4 text-gold shrink-0" />
                                <a href={`tel:${SITE.phone}`} className="hover:text-gold transition-colors">{SITE.phoneIntl}</a>
                            </li>
                            <li className="flex items-center gap-3 text-white/70">
                                <Mail className="w-4 h-4 text-gold shrink-0" />
                                <a href={`mailto:${SITE.email}`} className="hover:text-gold transition-colors break-all">{SITE.email}</a>
                            </li>
                            <li className="flex items-center gap-3 text-white/70">
                                <MessageCircle className="w-4 h-4 text-gold shrink-0" />
                                <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">WhatsApp Us</a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 items-center justify-between">
                    <p className="text-xs text-white/40">© {new Date().getFullYear()} Ihao Music House Delhi. All rights reserved.</p>
                    <p className="text-xs text-white/40">Best Music Classes in Malviya Nagar · Saket · South Delhi</p>
                </div>
            </div>
        </footer>
    );
}
